import { Box, Paper } from "@mui/material";
import type { ReactNode } from "react";
import type { ConsumptionChartModel } from "../../shared/consumption/components/consumption-chart/chartModel";
import { ConsumptionChartHeader } from "../../shared/consumption/components/consumption-chart/ConsumptionChartHeader";
import { PeriodBarsChart } from "../../shared/consumption/components/consumption-chart/PeriodBarsChart";
import type {
	UtilityConsumptionPeriod,
	UtilityConsumptionSummary,
} from "../../shared/consumption/models/UtilityConsumption";
import { formatMeasureWithUnit } from "../../shared/consumption/utils/format";

const getHoveredMeterLabel = (
	model: ConsumptionChartModel,
	meterId: string | null,
): string | null => {
	if (meterId === null) return null;

	const readings = (model.valuesByMeterId.get(meterId) ?? []).filter(
		(value): value is number => value !== null,
	);
	const total = readings.reduce((sum, value) => sum + value, 0);
	const scope =
		readings.length === 0
			? "no readings in the period"
			: total === 0
				? "no consumption in the period"
				: formatMeasureWithUnit(total, model.unit);

	return `${model.getMeterName(meterId)} · ${scope}`;
};

interface PeriodConsumptionPanelProps {
	model: ConsumptionChartModel;
	summary: UtilityConsumptionSummary;
	period: UtilityConsumptionPeriod;
	highlightedMeterId: string | null;
	controls: ReactNode;
	table: ReactNode;
	onMeterFilter: (meterId: string) => void;
}

export const PeriodConsumptionPanel = ({
	model,
	summary,
	period,
	highlightedMeterId,
	controls,
	table,
	onMeterFilter,
}: PeriodConsumptionPanelProps) => (
	<Paper
		component="section"
		variant="outlined"
		sx={{
			display: "flex",
			flex: 1,
			flexDirection: "column",
			minHeight: 0,
			minWidth: 0,
			p: 1.25,
		}}
	>
		<ConsumptionChartHeader
			period={period}
			isPartial={summary.IsMainPartial}
			total={summary.MainTotalGallons}
			unit={model.unit}
			meterScopeLabel={getHoveredMeterLabel(model, highlightedMeterId)}
		/>
		<Box
			sx={{
				alignItems: "center",
				display: "flex",
				flexWrap: "wrap",
				gap: 1,
				minWidth: 0,
				mt: 1,
				width: "100%",
			}}
		>
			{controls}
		</Box>
		<Box
			sx={{
				display: "grid",
				flex: 1,
				gap: 1.25,
				gridTemplateColumns: {
					xs: "minmax(0, 1fr)",
					lg: "repeat(2, minmax(0, 1fr))",
				},
				minHeight: 0,
				minWidth: 0,
				mt: 1,
			}}
		>
			<Box
				sx={{
					height: { xs: 260, lg: "auto" },
					minHeight: 0,
					minWidth: 0,
				}}
			>
				<PeriodBarsChart
					model={model}
					highlightedBucket={null}
					highlightedMeterId={highlightedMeterId}
					showLegend={false}
					onMeterFilter={onMeterFilter}
				/>
			</Box>
			<Box
				sx={{
					borderLeft: { lg: `1px solid ${"#E9EDF1"}` },
					borderTop: { xs: `1px solid ${"#E9EDF1"}`, lg: 0 },
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
					minWidth: 0,
					pl: { lg: 1.25 },
					pt: { xs: 1, lg: 0 },
				}}
			>
				{table}
			</Box>
		</Box>
	</Paper>
);
